import { pushEcommerceEvent } from '~/gtm/dataLayer';
import { mapOrderToGA4Items, getOrderValue, getShippingValue, getTaxValue } from '~/gtm/mappers/orderMapper';
import { mapProductToGA4Item } from '~/gtm/mappers/productMapper';
import type { GA4Item, ListContext } from '~/gtm/types';
import { shouldFirePurchase, markPurchaseFired } from '~/gtm/utils/deduplication';
import type { Order, OrderItem, ProductVariantDetails } from '~/types/Order';
import type { ApiProductVariant, Product, ProductVariantDetails as LegacyProductVariant } from '~/types/Product';

// Union type for variant - supports both legacy and new API variants
type ProductVariant = LegacyProductVariant | ApiProductVariant | null;

/**
 * Push view_item event when viewing a product detail page
 */
export function pushViewItem(
  product: Product,
  variant: ProductVariant,
  currency: string,
  breadcrumbPath?: { name: string; slug: string }[]
): void {
  const item = mapProductToGA4Item(product, variant, undefined, undefined, breadcrumbPath);
  const value = item.price ?? 0;

  pushEcommerceEvent('view_item', {
    currency,
    value,
    items: [item],
  });
}

/**
 * Push add_to_cart event
 */
export function pushAddToCart(
  product: Product,
  variant: ProductVariant,
  quantity: number,
  currency: string,
  listContext?: ListContext
): void {
  const item = mapProductToGA4Item(product, variant, listContext);
  item.quantity = quantity;
  const value = (item.price ?? 0) * quantity;

  pushEcommerceEvent('add_to_cart', {
    currency,
    value,
    items: [item],
  });
}

/**
 * Push remove_from_cart event
 */
export function pushRemoveFromCart(item: OrderItem, currency: string): void {
  const variant = item.variant as ProductVariantDetails | undefined;
  const variantCode = typeof item.variant === 'string' ? item.variant.split('/').pop() : variant?.code;

  const ga4Item: GA4Item = {
    item_id: variantCode || 'unknown',
    item_name: item.productName || 'Unknown Product',
    price: item.unitPrice ? item.unitPrice / 100 : undefined,
    quantity: item.quantity || 1,
  };

  // Add variant name from name or optionValues
  if (variant?.name) {
    ga4Item.item_variant = variant.name;
  } else if (variant?.optionValues && variant.optionValues.length > 0) {
    ga4Item.item_variant = variant.optionValues.map((ov) => ov.value).join(' / ');
  }

  const value = (ga4Item.price ?? 0) * (ga4Item.quantity ?? 1);

  pushEcommerceEvent('remove_from_cart', {
    currency,
    value,
    items: [ga4Item],
  });
}

/**
 * Push view_cart event
 */
export function pushViewCart(order: Order): void {
  const items = mapOrderToGA4Items(order);
  const value = getOrderValue(order);

  pushEcommerceEvent('view_cart', {
    currency: order.currencyCode,
    value,
    items,
  });
}

/**
 * Push begin_checkout event
 */
export function pushBeginCheckout(order: Order): void {
  const items = mapOrderToGA4Items(order);
  const value = getOrderValue(order);

  pushEcommerceEvent('begin_checkout', {
    currency: order.currencyCode,
    value,
    coupon: order.promotionCoupon?.code,
    items,
  });
}

/**
 * Push add_shipping_info event
 */
export function pushAddShippingInfo(order: Order, shippingTier: string): void {
  const items = mapOrderToGA4Items(order);
  const value = getOrderValue(order);

  pushEcommerceEvent('add_shipping_info', {
    currency: order.currencyCode,
    value,
    coupon: order.promotionCoupon?.code,
    shipping_tier: shippingTier,
    items,
  });
}

/**
 * Push add_payment_info event
 */
export function pushAddPaymentInfo(order: Order, paymentType: string): void {
  const items = mapOrderToGA4Items(order);
  const value = getOrderValue(order);

  pushEcommerceEvent('add_payment_info', {
    currency: order.currencyCode,
    value,
    coupon: order.promotionCoupon?.code,
    payment_type: paymentType,
    items,
  });
}

/**
 * Push purchase event with deduplication
 */
export function pushPurchase(order: Order): boolean {
  const transactionId = order.number;

  if (!transactionId) {
    console.warn('[GTM] Cannot push purchase: missing transaction ID');
    return false;
  }

  // Check if already fired
  if (!shouldFirePurchase(transactionId)) {
    return false;
  }

  const items = mapOrderToGA4Items(order);
  const value = getOrderValue(order);
  const shipping = getShippingValue(order);
  const tax = getTaxValue(order);

  pushEcommerceEvent('purchase', {
    transaction_id: transactionId,
    affiliation: 'NOVIQ Online Store',
    currency: order.currencyCode,
    value,
    tax,
    shipping,
    coupon: order.promotionCoupon?.code,
    items,
  });

  // Mark as fired
  markPurchaseFired(transactionId);
  return true;
}

/**
 * Push view_item_list event for product listings
 */
export function pushViewItemList(items: GA4Item[], listContext: ListContext, currency: string): void {
  if (items.length === 0) return;

  // Add list context to all items
  const itemsWithContext = items.map((item) => ({
    ...item,
    item_list_id: listContext.item_list_id,
    item_list_name: listContext.item_list_name,
  }));

  pushEcommerceEvent('view_item_list', {
    currency,
    item_list_id: listContext.item_list_id,
    item_list_name: listContext.item_list_name,
    items: itemsWithContext,
  });
}

/**
 * Push select_item event when clicking on a product in a list
 */
export function pushSelectItem(product: Product, listContext: ListContext, currency: string, index?: number): void {
  const item = mapProductToGA4Item(product, null, listContext, index);

  pushEcommerceEvent('select_item', {
    currency,
    item_list_id: listContext.item_list_id,
    item_list_name: listContext.item_list_name,
    items: [item],
  });
}
